import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/team", label: "Team" },
  { to: "/portfolio", label: "Portfolio" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color] duration-200 ${
        scrolled
          ? "bg-background/90 backdrop-blur border-b border-border"
          : "bg-background/60 backdrop-blur border-b border-transparent"
      }`}
    >
      <div className="container-x flex h-[72px] items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
          <span className="inline-block w-3 h-3 bg-foreground group-hover:bg-accent transition-colors" />
          <span className="font-display text-[18px] md:text-[20px] tracking-[0.25em] uppercase font-medium">
            SamStella
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-10" aria-label="Primary">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `font-mono-label text-[11px] tracking-[0.22em] uppercase transition-colors link-underline ${
                  isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <Link
          to="/contact"
          data-testid="navbar-cta-getintouch"
          className="hidden md:inline-flex items-center gap-2 px-4 py-2 border border-foreground text-foreground hover:bg-foreground hover:text-background font-mono-label text-[11px] tracking-[0.22em] uppercase transition-colors"
        >
          Get in Touch
        </Link>

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden inline-flex items-center justify-center w-10 h-10 border border-foreground"
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div
          className="md:hidden border-t border-border bg-background"
          data-testid="mobile-menu"
        >
          <nav className="container-x py-6 flex flex-col gap-5">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `font-mono-label text-xs tracking-[0.22em] uppercase ${
                    isActive ? "text-foreground" : "text-muted-foreground"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="mobile-cta-getintouch"
              className="mt-2 inline-flex items-center justify-center px-4 py-3 bg-foreground text-background font-mono-label text-[11px] tracking-[0.22em] uppercase"
            >
              Get in Touch
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
